<template>
  <div class="container-fluid">
    <page-header 
      class="mt-4 mt-md-0 mt-sm-4"
      left-side-class="col-xl-10"
      right-side-class="col-xl-2 text-right"
      :page="headerConfig"
    >
      <template v-slot:customActions>
        <ActionButton
          id="go-back-btn"
          i="fas fa-arrow-circle-left"
          variant="ab-btn  ab-btn-outline-success m-1"
          @click="$router.go(-1)"
        >
          Regresar
        </ActionButton>

        <ActionButton
          id="add-schedule-item-btn"
          text="Añadir"
          i="fas fa-file-plus"
          variant="success m-1"
          @click="handleAddScheduleItem"
        />
      </template>
    </page-header>

    <filter-manager
      v-model="filter"
      display-advanced
      :filters="filterConfig"
      @search="handleSearch"
    />

    <custom-separator
      class="pt-3 my-4"
      text="Resultados"
    />

    <custom-result-not-found
      v-if="!scheduleItems.length"
      text="No cuenta información en el sistema."
    />

    <ScheduleItemResultRow 
      v-for="item in scheduleItems"
      :key="item.id"
      :id="item.id"
      :area="item.area"
      :type="item.type"
      :title="item.title"
      :updatedDate="item.updatedDate"
      :updatedBy="item.updatedBy"
      :active="item.active"
      @edit="handleEdit"
      @toggle="handleToggleStatus"
    />
    
    <Pagination
      v-if="pagination.totalItemCount > pagination.perPage"
      id="pgcommentHistory"
      :show-text="false"
      :data="pagination"
      @pagination-go-page="handleSearch(null, $event)"
    />
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import Pagination from 'pagination';
import FilterManager from 'FilterManager';
import ActionButton from 'ActionButton';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import ScheduleItemResultRow from './components/ScheduleItemResultRow';

import alert from "@/mixin/alert.mixin";
import { unRegisterMixin } from '@/mixin/storeGenerator';
import { getInitFilter, search, toggle } from "@/services/operations/public-portal-schedule-items";

const DEFAULT_ITEMS_PER_PAGE = 20;
const FATHERS_AREA_ID = 1;
const EVENT_TYPE_ID = 4;
const SCHEDULE_ITEM_NEW_ROUTE_NAME = 'operationPublicPortalNewItem';
const SCHEDULE_ITEM_EDIT_ROUTE_NAME = 'operationPublicPortalEditItem';

export default {
  name: 'SearchScheduleItems',
  mixins: [alert, unRegisterMixin],
  components: {
    PageHeader,
    Pagination,
    ActionButton,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    ScheduleItemResultRow
  },
  data() {
    return {
      headerConfig: {
        title: 'Mantenimiento del Portal Público',
        icon: 'fas fa-calculator',
        routes: ['Inicio', 'Operaciones', 'Mantenimiento del Portal Público'],
      },
      options: {
        area: [],
        type: [],
        category: [],
        region: [],
        tag: [],
      },
      filter: {
        area: null,
        type: null,
        searchText: null,
        category: null,
        tag: null,
        region: null,
        finishDate: null,
        publicationDate: null
      },
      scheduleItems: [],
      pagination: {
        perPage: DEFAULT_ITEMS_PER_PAGE,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
      },
    };
  },
  computed: {
    typeOptions() {
      const options = this.options.type.map(type => ({
        id: type.id,
        name: type.name
      }));

      if (this.filter.area?.id != FATHERS_AREA_ID) return options;
      
      return options.filter(opt => opt.id != EVENT_TYPE_ID);
    },
    filterConfig() {
      return [
        {
          name: 'area',
          component: 'CustomDropDown',
          class: 'col-md-3',
          initialValue: null,
          options: this.options.area.map(area => ({
            id: area.id,
            name: area.name
          })),
          placeholder: 'Área',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
        },
        {
          name: 'type',
          component: 'CustomDropDown',
          class: 'col-md-3',
          initialValue: null,
          options: this.typeOptions,
          placeholder: 'Tipo',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
        },
        {
          name: 'searchText',
          component: 'CustomInput',
          placeholder: 'Escriba título de la noticia, taller o evento',
          icon: 'fa fa-search orange-icon',
          leftIcon: true,
          class: 'col-md-6',
          type: 'text',
        },
        {
          name: "category",
          label: "Categoría:",
          component: "CustomDropDown",
          class: "col-md-4",
          options: this.options.category.map(category => ({
            id: category.id,
            name: category.name
          })),
          placeholder: "Seleccione",
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: true,
        },
        {
          name: "tag",
          label: "Etiqueta:",
          component: "CustomDropDown",
          class: "col-md-4",
          options: this.options.tag.map(tag => ({
            id: tag.id,
            name: tag.name
          })),
          placeholder: "Seleccione",
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: true,
        },
        {
          name: "region",
          label: "Región:",
          component: "CustomDropDown",
          class: "col-md-4",
          options: this.options.region.map(region => ({
            id: region.id,
            name: region.name
          })),
          placeholder: "Seleccione",
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: true,
        },
        {
          name: "publicationDate",
          label: "Fecha de Publicación:",
          component: "CustomDatePicker",
          class: "col-md-4",
          classContainer: "w-100",
          placeholder: "DD/MM/YYYY",
          isAdvanced: true,
        },
        {
          name: "finishDate",
          label: "Fecha de Finalización:",
          component: "CustomDatePicker",
          class: "col-md-4",
          classContainer: "w-100",
          placeholder: "DD/MM/YYYY",
          isAdvanced: true,
        },
      ]
    },
  },
  methods: {
    async fetchInitFilters() {
      let { data } = await getInitFilter();
      this.options = {
        area: data.areaOptions,
        category: data.categoryOptions,
        region: data.regionOptions,
        tag: data.tagOptions,
        type: data.typeOptions
      };
    },
    async handleSearch(filter = null, page = 1) {
      const filters = filter ? filter : this.filter;
      const payload = {
        pageIndex: page,
        pageSize: this.pagination.perPage,
        areaId: filters.area?.id,
        typeId: filters.type?.id,
        categoryId: filters.category?.id,
        tagId: filters.tag?.id,
        regionId: filters.region?.id,
        startDate: filters.publicationDate,
        finishDate: filters.finishDate,
        searchText: filters.searchText
      };
      const { data } = await search(payload);
      
      this.scheduleItems = [...data.data];

      this.pagination.totalItemCount = data.totalItemCount;
      this.pagination.count = data.count;
      this.pagination.currentPage = data.currentPage;
      this.pagination.pageCount = data.pageCount;
    },
    async handleToggleStatus(id) {
      let scheduleItem = this.scheduleItems.find(scheduleItem => scheduleItem.id == id);

      let response = await this.ShowCancelModal(
        "Confirmación",
        `¿Está seguro(a) que desea ${scheduleItem.active ? 'inactivar' : 'reactivar'} la publicación?`,
        "Si",
        "No"
      );

      if (!response) return;

      await toggle(id);      
      await this.handleSearch(
        this.filter,
        this.pagination.currentPage);
    },
    handleAddScheduleItem() {
      this.$router.push({ name: SCHEDULE_ITEM_NEW_ROUTE_NAME });
    },
    handleEdit(id) {
      this.$router.push({ name: SCHEDULE_ITEM_EDIT_ROUTE_NAME, params: { id } });
    },
  },
  watch: {
    'filter.area'(newVal, oldVal) {
      if (newVal?.id == FATHERS_AREA_ID && this.filter.type?.id == EVENT_TYPE_ID)
      {
        this.filter.type = null;
      }
    }
  },
  async mounted() {
    await this.fetchInitFilters();
    await this.handleSearch();
  }
};
</script>